import * as React from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Chip,
  Menu,
  MenuItem,
  IconButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Badge,
} from "@mui/material";
import {
  Favorite,
  Person,
  Settings,
  Payment,
  AdminPanelSettings,
  Logout,
  AccountBalanceWallet,
  SupportAgent,
  NotificationsOutlined,
} from "@mui/icons-material";

type Props = {
  hearts: number;
  unreadNotifications: number;
  userRole?: string;
  profileImageUrl?: string;
  displayName: string;
  onHeartsPurchase: () => void;
  onProfile: () => void;
  onAdmin?: () => void;
  onLogout: () => void;
  onWallet?: () => void;
  onSettings?: () => void;
  onSupport?: () => void;
  onNotifications?: () => void;
  loading?: boolean;
};

export default function DashboardHeader({
  hearts,
  unreadNotifications,
  userRole,
  profileImageUrl,
  displayName,
  onHeartsPurchase,
  onProfile,
  onAdmin,
  onLogout,
  onWallet,
  onSettings,
  onSupport,
  onNotifications,
  loading = false,
}: Props) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuClick = (action: () => void) => {
    action();
    handleMenuClose();
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: '#0D1F3C',
        color: 'white',
        top: 0,
        zIndex: 1100,
        borderRadius: 0,
      }}
    >
      <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
        <Typography
          variant="h6"
          component="div"
          sx={{
            flexGrow: 1,
            fontFamily: '"Playfair Display", serif',
            fontWeight: 700,
            color: 'white',
            letterSpacing: '0.04em',
            fontSize: { xs: '1.1rem', sm: '1.25rem' }
          }}
        >
          Silver<Box component="span" sx={{ color: '#C9A86C' }}>Connect</Box>
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
          {/* 하트 잔액 위젯 */}
          {loading ? (
            <Skeleton
              variant="rounded"
              width={80}
              height={32}
              sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}
            />
          ) : (
            <Box
              component="button"
              onClick={onWallet ?? onHeartsPurchase}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                border: '1px solid rgba(201,168,108,0.4)',
                borderRadius: '20px',
                px: 1.5,
                py: 0.75,
                color: '#E8CFA0',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                bgcolor: 'transparent',
                transition: 'border-color 0.2s',
                '&:hover': { borderColor: '#C9A86C' },
              }}
            >
              🤍 {hearts}
            </Box>
          )}

          {/* 알림 벨 */}
          <IconButton
            onClick={onNotifications}
            size="small"
            sx={{ p: { xs: 0.5, sm: 0.75 }, color: 'rgba(255,255,255,0.8)' }}
            aria-label="알림"
          >
            <Badge
              badgeContent={unreadNotifications > 0 ? unreadNotifications : null}
              max={99}
              sx={{
                '& .MuiBadge-badge': {
                  bgcolor: '#FF6B6B',
                  color: 'white',
                  fontSize: '10px',
                  minWidth: '16px',
                  height: '16px',
                },
              }}
            >
              <NotificationsOutlined sx={{ fontSize: { xs: 22, sm: 24 } }} />
            </Badge>
          </IconButton>

          {/* 사용자 메뉴 */}
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleMenuOpen}
            size="small"
            sx={{ p: { xs: 0.5, sm: 1 } }}
          >
            {loading ? (
              <Skeleton
                variant="circular"
                width={32}
                height={32}
                sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}
              />
            ) : (
              <Box
                sx={{
                  width: { xs: 32, sm: 34 },
                  height: { xs: 32, sm: 34 },
                  borderRadius: '50%',
                  border: '1.5px solid #C9A86C',
                  bgcolor: 'rgba(201,168,108,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: '"Playfair Display", serif',
                  fontSize: { xs: 13, sm: 15 },
                  color: '#C9A86C',
                  userSelect: 'none',
                }}
              >
                {displayName?.[0] || '김'}
              </Box>
            )}
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: { minWidth: 200 }
            }}
          >
            <MenuItem onClick={() => handleMenuClick(onProfile)}>
              <ListItemIcon>
                <Person fontSize="small" />
              </ListItemIcon>
              <ListItemText>프로필 설정</ListItemText>
            </MenuItem>

            {onWallet && (
              <MenuItem onClick={() => handleMenuClick(onWallet)}>
                <ListItemIcon>
                  <AccountBalanceWallet fontSize="small" />
                </ListItemIcon>
                <ListItemText>내 지갑</ListItemText>
              </MenuItem>
            )}

            <MenuItem onClick={() => handleMenuClick(onHeartsPurchase)}>
              <ListItemIcon>
                <Payment fontSize="small" />
              </ListItemIcon>
              <ListItemText>하트 구매</ListItemText>
            </MenuItem>

            {onSupport && (
              <MenuItem onClick={() => handleMenuClick(onSupport)}>
                <ListItemIcon>
                  <SupportAgent fontSize="small" />
                </ListItemIcon>
                <ListItemText>고객센터</ListItemText>
              </MenuItem>
            )}

            {onSettings && (
              <MenuItem onClick={() => handleMenuClick(onSettings)}>
                <ListItemIcon>
                  <Settings fontSize="small" />
                </ListItemIcon>
                <ListItemText>설정</ListItemText>
              </MenuItem>
            )}

            {userRole === 'ADMIN' && onAdmin && (
              <MenuItem onClick={() => handleMenuClick(onAdmin)}>
                <ListItemIcon>
                  <AdminPanelSettings fontSize="small" />
                </ListItemIcon>
                <ListItemText>관리자 페이지</ListItemText>
              </MenuItem>
            )}

            <MenuItem onClick={() => handleMenuClick(onLogout)} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <Logout fontSize="small" sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText>로그아웃</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
